import jsCookie from './jsc-converter';
import { canDropCookie, getDomainName, getPolicyVersion, setStore } from './helpers';
import { HAS_CONSENTED, WSCR_COOKIE_CONSENT, WSCR_COOKIE_CONSENT_CATEGORIES, WSCR_COOKIE_CONSENT_PROPS } from '../constants';
export { setStore } from './helpers';
const localDomains = [
    'localhost',
    // https://docs.docker.com/desktop/mac/networking/#use-cases-and-workarounds
    'host.docker.internal',
    '127.0.0.1',
    ''
];
const isNumber = n => !isNaN(parseFloat(n)) && isFinite(n);
export let cookieOptionsList;
/**
 * @param {!string} name
 * @return {string}
 */
const get = name => jsCookie.get(name);
/**
 * syntactic sugar for JSCookie.set
 * @param {!string} name            Cookie name
 * @param {!string|!object} data    Cookie value
 * @param {!object} optionOverload  (Optional) Hash of cookie metadata to overload default or presets
 * @return {void}
 */
const set = (name, data, optionOverload) => {
    const options = name in cookieOptionsList ? cookieOptionsList[name] : cookieOptionsList.default;
    if (optionOverload) {
        for (const k in optionOverload) {
            if (optionOverload.hasOwnProperty(k)) {
                options[k] = optionOverload[k];
            }
        }
    }
    jsCookie.set(name, data, options);
};
/**
 * Will parse wscrCookieConsent if it and hasConsented cookie are present.
 * wscrCookieConsent contains the following properties
 * - 1: represents "Strictly Necessary" cookies. This cookie value is ALWAYS true
 * - 2: represents "Site Experience" cookies. Can return true/false
 * - 3: represents "Performance & Operation" cookies. Can return true/false
 * - 4: represents "Marketing, personalization & advertising" cookies. Can return true/false
 * - 5: represents "Visa Cookie notice". This cookie value is ALWAYS true
 * - version: represents Digital Control Room policy version
 * Example wscrCookieConsent cookie value: 1=true&2=false&3=false&4=false&5=false&version=20200709-001
 * @return {object} -- if wscrCookieConsent is not present, will return an empty object.
 * if wscrCookieConsent is present, will return the cookie key/values parsed in an object
 */
export const getParsedWSCRCookieConsent = () => {
    const hasConsented = get(HAS_CONSENTED);
    const wscrCookie = get(WSCR_COOKIE_CONSENT);
    let parsedHasConsented;
    try {
        parsedHasConsented = JSON.parse(hasConsented);
    }
    catch (err) {
        // fail silently
    }
    if (parsedHasConsented && parsedHasConsented.consent && typeof wscrCookie === 'string') {
        return wscrCookie.split('&').reduce((accum, curValue) => {
            const value = curValue.split('=');
            if (value.length > 1) {
                const key = value[0].trim();
                if (Object.values(WSCR_COOKIE_CONSENT_PROPS).includes(key)) {
                    accum[key] = isNumber(key) ? value[1].toLowerCase().trim() === 'true' : value[1].trim();
                }
            }
            return accum;
        }, {});
    }
    return {};
};
/**
 * Helper function that checks parsed wscrCookieConsent category values
 * @param {function} conditionCallback -- callback that should return a boolean; will be passed cookie value
 * @returns {function} returns a function expecting a cookie name
 */
const checkWscrCookieConsent = conditionCallback => (cookieName, returnBool = true) => {
    const parsedWSCRCookie = getParsedWSCRCookieConsent();
    if (typeof cookieName === 'string' &&
        cookieName.length &&
        parsedWSCRCookie[WSCR_COOKIE_CONSENT_PROPS.STRICTLY_NECESSARY]) {
        for (const key in WSCR_COOKIE_CONSENT_CATEGORIES) {
            const normalizedCookieList = WSCR_COOKIE_CONSENT_CATEGORIES[key].map(cookie => cookie.toLowerCase().trim());
            if (key !== 'STRICTLY_NECESSARY' &&
                normalizedCookieList.includes(cookieName.toLowerCase().trim()) &&
                conditionCallback(parsedWSCRCookie[WSCR_COOKIE_CONSENT_PROPS[key]])) {
                return false;
            }
        }
    }
    return returnBool;
};
export const setCookieCheckWscrCookieConsent = checkWscrCookieConsent(cookieValue => !cookieValue);
export const removeCookieCheckWscrCookieConsent = checkWscrCookieConsent(cookieValue => cookieValue);
/**
 * EU gate-keeper version of this call
 * @param {!string} name Cookie name
 * @param {!object|!string|!number} data Cookie value
 * @param {!object} optionOverload  (Optional) Hash of cookie metadata to overload default or presets
 * @return {void}
 */
const setCookie_eu = (name, data, optionOverload) => {
    if (data !== null && data !== undefined) {
        const doSet = setCookieCheckWscrCookieConsent(name, canDropCookie()) ||
            WSCR_COOKIE_CONSENT_CATEGORIES.STRICTLY_NECESSARY.includes(name);
        if (doSet) {
            set(name, data, optionOverload);
        }
        else {
            // eslint-disable-next-line no-console
            console.warn(`[ ${name} ] was not set as the user has not consented to cookie storage and the cookie was deemed to be non-essential`);
        }
    }
};
/**
 * We are moving cookies from secure.visa.com to visa.com and to make sure that when we remove the cookies.
 * we are removing all the new and legacy cookies, we are going to always delete the cookie form both domains.
 * @param {!string} name
 */
const remove = name => {
    if (removeCookieCheckWscrCookieConsent(name)) {
        if (name in cookieOptionsList) {
            jsCookie.remove(name, cookieOptionsList[name]);
        }
        jsCookie.remove(name, cookieOptionsList.default);
    }
};
/**
 * sets the `hasConsented` cookie for EU compliance
 * @param {!boolean} consentFlag
 * @return {void}
 */
export const setConsentCookie = consentFlag => {
    // istanbul ignore next
    const consent = typeof consentFlag === 'undefined' ? false : consentFlag;
    const policyVersion = getPolicyVersion();
    if (consent && !get(WSCR_COOKIE_CONSENT)) {
        set(WSCR_COOKIE_CONSENT, `1=true&2=true&3=true&4=true&5=true&version=${policyVersion}`, {
            expires: 364
        });
    }
    set(HAS_CONSENTED, JSON.stringify({
        consent,
        optedIn: consent,
        policyVersion
    }), {
        expires: 364
    });
};
/**
 * cookie sanity check
 * @return {boolean}
 */
export const canReadAndWrite = () => {
    let check = false;
    const COOKIE_TEST = 'test';
    set(COOKIE_TEST, COOKIE_TEST);
    if (get(COOKIE_TEST) === COOKIE_TEST) {
        remove(COOKIE_TEST);
        check = true;
    }
    return check;
};
/**
 * Updates wscrCookieConsent category cookies if the cookie is present
 * For "Performance & Operation" cookies we can only remove and cannot set them
 * @param {object} cookieData
 * @returns {void}
 */
export const updateWSCRCategoryCookies = (cookieData = {}) => {
    const parsedWSCRCookie = getParsedWSCRCookieConsent();
    // wscrCookieConsent category, "Strictly necessary" will always return true
    // In Destination, the toggle for "Strictly necessary" cannot be changed
    const isHasConsentAndWscrCookiePresent = parsedWSCRCookie[WSCR_COOKIE_CONSENT_PROPS.STRICTLY_NECESSARY];
    if (isHasConsentAndWscrCookiePresent) {
        // Toggle "Site Experience" cookies
        WSCR_COOKIE_CONSENT_CATEGORIES.SITE_EXPERIENCE.forEach(cookie => parsedWSCRCookie[WSCR_COOKIE_CONSENT_PROPS.SITE_EXPERIENCE]
            ? setCookie_eu(cookie, cookieData[cookie])
            : remove(cookie));
        // Remove "Performance and Operation" cookies
        if (!parsedWSCRCookie[WSCR_COOKIE_CONSENT_PROPS.PERFORMANCE_AND_OPERATION]) {
            WSCR_COOKIE_CONSENT_CATEGORIES.PERFORMANCE_AND_OPERATION.forEach(cookie => remove(cookie));
        }
        // Remove "Marketing, personalization & advertising" cookies
        if (!parsedWSCRCookie[WSCR_COOKIE_CONSENT_PROPS.MARKETING_PERSONALIZATION_AND_ADVERTISING]) {
            WSCR_COOKIE_CONSENT_CATEGORIES.MARKETING_PERSONALIZATION_AND_ADVERTISING.forEach(cookie => remove(cookie));
        }
    }
};
/**
 * Sets up cookie domains and options
 */
export const initialize = () => {
    const loc = location.hostname;
    const prot = location.protocol.split(':')[0];
    const ONE_YEAR = 52 * 7;
    const SIX_MONTHS = (52 / 2) * 7;
    const THREE_MONTHS = (52 / 4) * 7;
    const TWO_MONTHS = (52 / 6) * 7;
    const THIRTY_MINUTES = 1 / 48;
    // If you are running on localhost, you cannot actually use the string 'localhost'
    // to set a cookie. It needs to be left out completely.
    const isLocal = localDomains.indexOf(loc) > -1;
    // istanbul ignore next
    const cookieDomain = isLocal ? undefined : getDomainName(loc, false);
    // istanbul ignore next
    const cookieSecureDomain = isLocal ? undefined : getDomainName(loc, true);
    const base = {
        expires: 365,
        path: '/',
        samesite: prot === 'https' ? 'None' : null,
        secure: prot === 'https'
    };
    const cookieOptions = Object.assign(Object.assign({}, base), { domain: cookieDomain });
    const cookieSecureOptions = Object.assign(Object.assign({}, base), { domain: cookieSecureDomain });
    const sessionCookieOptions = Object.assign(Object.assign({}, cookieOptions), { expires: undefined });
    // curried to module scope
    cookieOptionsList = {
        VCO_apikey: Object.assign({}, sessionCookieOptions),
        VCO_correlationId: Object.assign({}, sessionCookieOptions),
        VCO_visitId: Object.assign({}, sessionCookieOptions),
        __cf_bm: Object.assign(Object.assign({}, cookieSecureOptions), { expires: THIRTY_MINUTES }),
        __cfduid: Object.assign(Object.assign({}, cookieSecureOptions), { expires: ONE_YEAR }),
        __cfruid: Object.assign({}, sessionCookieOptions),
        _art: Object.assign({}, cookieSecureOptions),
        _did: Object.assign({}, cookieSecureOptions),
        _ga: Object.assign(Object.assign({}, cookieSecureOptions), { expires: ONE_YEAR }),
        _gcl_au: Object.assign(Object.assign({}, cookieSecureOptions), { expires: THREE_MONTHS }),
        _gid: Object.assign(Object.assign({}, cookieSecureOptions), { expires: ONE_YEAR }),
        actionindicator: Object.assign({}, sessionCookieOptions),
        alwaysRemember: Object.assign({}, cookieSecureOptions),
        campaign_tracking: Object.assign(Object.assign({}, cookieOptions), { expires: 3 }),
        country_preference: Object.assign({}, cookieOptions),
        default: Object.assign({}, cookieOptions),
        ermEnabled: Object.assign(Object.assign({}, cookieSecureOptions), { expires: ONE_YEAR }),
        fedid: Object.assign({}, sessionCookieOptions),
        fpusername: Object.assign(Object.assign({}, cookieOptions), { expires: 1 }),
        iframe: Object.assign({}, sessionCookieOptions),
        iscAuthEnabled: Object.assign(Object.assign({}, cookieOptions), { expires: THIRTY_MINUTES }),
        locale: Object.assign({}, cookieOptions),
        optimizelyEndUserId: Object.assign(Object.assign({}, cookieSecureOptions), { expires: SIX_MONTHS }),
        ssiDeclined: Object.assign(Object.assign({}, cookieSecureOptions), { expires: TWO_MONTHS }),
        ssiEnabled: Object.assign(Object.assign({}, cookieSecureOptions), { expires: SIX_MONTHS }),
        wscrCookieConsent: {}
    };
};
// istanbul ignore next
const bootstrap = (isEuroUser, canDropCookie, whitelistCookies) => {
    if (Array.isArray(whitelistCookies)) {
        whitelistCookies.forEach(cookie => {
            if (!WSCR_COOKIE_CONSENT_CATEGORIES.STRICTLY_NECESSARY.includes(cookie)) {
                WSCR_COOKIE_CONSENT_CATEGORIES.STRICTLY_NECESSARY.push(cookie);
            }
        });
    }
};
const dropNonEssentialCookies = () => {
    const cookies = jsCookie.get();
    if (cookies && Object.keys(cookies).length > 0) {
        for (const cookie in cookies) {
            if (!WSCR_COOKIE_CONSENT_CATEGORIES.STRICTLY_NECESSARY.includes(cookie)) {
                remove(cookie);
            }
        }
    }
};
// Set defaults for production, the window location will always be used
initialize();
export { canDropCookie };
export default {
    bootstrap,
    canReadAndWrite,
    dropNonEssentialCookies,
    get,
    getCookie: get,
    remove,
    removeCookie: remove,
    set: setCookie_eu,
    setConsentCookie,
    setCookie: setCookie_eu,
    setStore
};
export const removeCookie = remove;
export const setCookie = setCookie_eu;
export const getCookie = get;
//# sourceMappingURL=index.js.map